import { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../hooks/AuthContext';

/**
 * Custom hook for submitting the assessment data along with selected tools.
 * @returns {Object} - Provides submit function, loading, error, and success states.
 */
const useSubmitAssessment = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const { authData } = useAuth();
  const access_token = authData?.access_token || authData?.accessToken;

  const submitAssessment = async (assessmentData, toolsUsed, packageId = null) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    const API_URL = "https://app.spiralreports.com/api/evaluations/new";

    try {
      // Determine if this is a packaged assessment
      const isPackaged = !!packageId;

      // Prepare request body
      const requestBody = {
        assessmentId: assessmentData.assessmentId,
        receipient: assessmentData.receipient || "SELF",
        name: assessmentData.name || "string",
        compliance: assessmentData.compliance || "string",
        response: assessmentData.questions.map((question) => ({
          question: question.question,
          considerScore: question.considerScore || true,
          options: question.options.map((option) => ({
            text: option.text,
            level: option.level,
          })),
        })),
        toolsUsed: toolsUsed,
        is_packaged: isPackaged,
        is_draft: false,
        // Include package_id if this is a packaged assessment
        ...(isPackaged && { package_id: packageId }),
        // Include package_eval_id if it exists in assessmentData
        ...(assessmentData.package_eval_id && { 
          package_eval_id: assessmentData.package_eval_id 
        })
      };

      // API call
      const response = await axios.post(API_URL, requestBody, {
        headers: {
          'Authorization': `Bearer ${access_token}`,
          'Content-Type': 'application/json',
        },
      });

      setSuccess(true);
      setLoading(false);
      return response.data;
    } catch (err) {
      setError('Error submitting the assessment. Please try again.');
      setLoading(false);
      throw err;
    }
  };

  return {
    submitAssessment,
    loading,
    error,
    success,
  };
};

export default useSubmitAssessment;